import { useState, useEffect, useCallback } from 'react'
import { Shield, CircleCheck, RefreshCw } from 'lucide-react'
import { cn } from '../lib/cn'

const SITE_KEY = import.meta.env.VITE_DOCAPTCHA_SITE_KEY ?? ''
const API_URL =
  import.meta.env.VITE_DOCAPTCHA_API_URL ??
  'https://api.docaptcha.dev/api/v1'

type Props = {
  onVerify: (token: string) => void
  onError?: () => void
}

/*
 * doCaptcha verification widget (Doable, doWallet DNA).
 * Light card, flat (ring), volt for the working state, success-ink when
 * verified, destructive-soft on error. The proof-of-work solver and the
 * challenge/verify calls are the official flow and must stay untouched.
 */

// ── Proof-of-work solver ─────────────────────────────────────
// Mirrors the official doCaptcha widget (docaptcha-website,
// packages/widget/src/pow.ts). The backend publishes
//   value = sha256(salt + n + challengeType + encodeParams(hints))
// and we search n in [0, maxNumber]. The salt carries "?expires=…"
// which is part of the hashed salt string but NOT of the hints.
type PowChallenge = {
  algorithm: string
  value: string
  maxNumber: number
  salt: string
  signature: string
  challengeType?: string
  behaviorHints?: Record<string, string>
}

async function sha256Hex(input: string): Promise<string> {
  const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input))
  return Array.from(new Uint8Array(buffer)).map((b) => b.toString(16).padStart(2, '0')).join('')
}

function parseSaltParams(salt: string): Record<string, string> {
  const out: Record<string, string> = {}
  const q = salt.indexOf('?')
  if (q < 0) return out
  for (const seg of salt.substring(q + 1).split('&')) {
    const eq = seg.indexOf('=')
    if (eq <= 0) continue
    out[seg.substring(0, eq)] = seg.substring(eq + 1)
  }
  return out
}

/** Returns the base64 JSON payload the verify endpoint expects. */
async function solveProofOfWork(
  challenge: PowChallenge,
  onProgress: (fraction: number) => void,
): Promise<string> {
  const algorithm = (challenge.algorithm || 'SHA256').replace('SHA-256', 'SHA256')
  if (algorithm !== 'SHA256') throw new Error(`Unsupported algorithm: ${challenge.algorithm}`)

  const params = parseSaltParams(challenge.salt)
  const challengeType = challenge.challengeType || params.type || 'NUMERIC_HASH'
  const hints: Record<string, string> = { ...params, ...(challenge.behaviorHints ?? {}) }
  delete hints.expires
  const encodedHints = Object.keys(hints).sort().map((k) => `${k}=${hints[k]}`).join('&')

  for (let n = 0; n <= challenge.maxNumber; n++) {
    const candidate = await sha256Hex(challenge.salt + n + challengeType + encodedHints)
    if (candidate === challenge.value) {
      const payload: Record<string, unknown> = {
        algorithm: challenge.algorithm,
        challenge: challenge.value,
        number: n,
        salt: challenge.salt,
        signature: challenge.signature,
        ...hints,
      }
      return btoa(JSON.stringify(payload))
    }
    // Yield so the spinner repaints; report progress every ~8k hashes.
    if ((n & 1023) === 0) await Promise.resolve()
    if (n > 0 && n % 8192 === 0) onProgress(n / challenge.maxNumber)
  }
  throw new Error('No solution found within maxNumber')
}


export function DoCaptcha({ onVerify, onError }: Props) {
  const [status, setStatus] = useState<
    'loading' | 'analyzing' | 'verified' | 'error'
  >('loading')
  const [progress, setProgress] = useState(0)
  const [confidence, setConfidence] = useState(0)
  const [behaviors, setBehaviors] = useState<string[]>([])
  const [errorMsg, setErrorMsg] = useState('')

  const allBehaviors: [string, string, string, string] = [
    'Natural mouse movements',
    'Human-like timing patterns',
    'Realistic interaction sequences',
    'Genuine browsing behavior',
  ]


  const generateFingerprint = useCallback((): string => {
    const data = [
      navigator.userAgent,
      navigator.language,
      `${screen.width}x${screen.height}`,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    ].join('|')
    let hash = 0
    for (let i = 0; i < data.length; i++) {
      hash = (hash << 5) - hash + data.charCodeAt(i)
      hash |= 0
    }
    return Math.abs(hash).toString(36)
  }, [])

  const verify = useCallback(async () => {
    setStatus('analyzing')
    setProgress(0)
    setConfidence(0)
    setBehaviors([])
    setErrorMsg('')

    // Progress is driven by the real proof-of-work search (fraction of the
    // search space consumed), not by a timer. The behaviour list fills in
    // as the solve advances so the UI keeps its shape.
    const report = (fraction: number) => {
      const p = Math.min(100, Math.round(fraction * 100))
      setProgress(p)
      const stage = p > 90 ? 4 : p > 70 ? 3 : p > 45 ? 2 : p > 20 ? 1 : 0
      setBehaviors(allBehaviors.slice(0, stage))
      setConfidence([0, 75, 86, 93, 97][stage] ?? 0)
    }

    try {
      const fingerprint = generateFingerprint()
      const challengeData = await fetch(`${API_URL}/api/v2/captcha/challenge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Site-Key': SITE_KEY },
        body: JSON.stringify({ siteKey: SITE_KEY, fingerprint, behaviorSignals: { mouseEvents: true, keyboardEvents: true } }),
      }).then(async (res) => {
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || `Failed (${res.status})`)
        return res.json()
      })

      if (challengeData.type === 'INVISIBLE_PASS') {
        report(1)
        setStatus('verified'); onVerify(challengeData.token || challengeData.challengeId); return
      }
      if (challengeData.type === 'RATE_LIMITED') throw new Error('Too many attempts.')

      const pow = challengeData.powChallenge
      if (!pow) throw new Error('No challenge data')

      const solved = await solveProofOfWork(pow, report)

      const verifyRes = await fetch(`${API_URL}/api/v2/captcha/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Site-Key': SITE_KEY },
        body: JSON.stringify({ challengeId: challengeData.challengeId, siteKey: SITE_KEY, solution: solved, fingerprint }),
      })

      if (!verifyRes.ok) throw new Error((await verifyRes.json().catch(() => ({}))).message || 'Verification failed')
      const result = await verifyRes.json()
      if (result.verified || result.success || result.token) {
        report(1)
        setStatus('verified'); onVerify(result.token || challengeData.challengeId)
      } else throw new Error(result.reason || result.message || 'Rejected')
    } catch (err: unknown) {
      setStatus('error')
      setErrorMsg(err instanceof Error ? err.message : 'Verification failed')
      onError?.()
    }
  }, [generateFingerprint, onVerify, onError])

  useEffect(() => { if (SITE_KEY) verify() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (!SITE_KEY) return null

  const verified = status === 'verified'

  const Badge = () => (
    <div className="flex flex-col items-center gap-0.5">
      <Shield className={cn('size-3.5', verified ? 'text-success-ink' : 'text-foreground')} strokeWidth={2} />
      <span className="text-center text-[7px] font-semibold leading-[1.2] text-muted-foreground">
        Protected by<br />doCaptcha
      </span>
    </div>
  )

  const Divider = () => <div className="h-6 w-px bg-border" />

  // ── LOADING / ANALYZING ───────────────────────────────────
  if (status === 'loading' || status === 'analyzing') {
    return (
      <div className="relative mx-auto my-3 w-full max-w-sm">
        <div className="flex items-center gap-3 rounded-xl bg-card py-2.5 pr-3 pl-4 ring-1 ring-foreground/10">
          <div className="size-[18px] shrink-0 animate-spin rounded-full border-2 border-border border-t-volt" />
          <div className="flex-1">
            <p className="text-[13px] font-medium text-foreground">Checking…</p>
          </div>
          <Divider />
          <Badge />
        </div>

        {status === 'analyzing' && (
          <div className="absolute inset-x-0 top-full z-50 mt-2">
            <div className="rounded-xl bg-popover p-3 text-popover-foreground ring-1 ring-foreground/10 shadow-float">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[11px] font-semibold text-foreground">Auto-Check Analysis</span>
                <span className="text-[10px] text-muted-foreground">Solving challenge</span>
              </div>
              <div className="mb-2 flex justify-center">
                <div className="size-9 animate-spin rounded-full border-[3px] border-border border-t-volt" />
              </div>
              <div className="mb-1 h-[5px] rounded-full bg-muted">
                <div className="h-[5px] rounded-full bg-volt transition-all duration-200" style={{ width: `${progress}%` }} />
              </div>
              <p className="mb-2 text-center text-[10px] font-medium text-muted-foreground">
                {Math.round(progress)}% · {confidence}% confidence
              </p>
              <div className="rounded-lg bg-muted/60 p-2">
                <div className="mb-1 flex items-center gap-1">
                  <Shield className="size-2.5 text-foreground" strokeWidth={2} />
                  <span className="text-[10px] font-semibold text-foreground">Detected Behaviors</span>
                </div>
                <div className="space-y-px">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="flex h-4 items-center gap-1">
                      {behaviors[i] ? (
                        <>
                          <CircleCheck className="size-2.5 text-success" strokeWidth={2.5} />
                          <span className="text-[10px] text-muted-foreground">{behaviors[i]}</span>
                        </>
                      ) : (
                        <>
                          <div className="size-2.5 rounded-full border border-border" />
                          <span className="text-[10px] text-muted-foreground/60">Analyzing…</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── VERIFIED ──────────────────────────────────────────────
  if (status === 'verified') {
    return (
      <div className="mx-auto my-3 w-full max-w-sm">
        <div className="flex items-center gap-3 rounded-xl bg-card py-2.5 pr-3 pl-4 ring-1 ring-foreground/10">
          <CircleCheck className="size-[18px] shrink-0 text-success-ink" strokeWidth={2} />
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-success-ink">Verified</p>
            <p className="text-[10px] text-muted-foreground">Human confirmed</p>
          </div>
          <Divider />
          <Badge />
        </div>
      </div>
    )
  }

  // ── ERROR ─────────────────────────────────────────────────
  return (
    <div className="mx-auto my-3 w-full max-w-sm">
      <button
        type="button"
        onClick={verify}
        className="flex w-full items-center gap-3 rounded-xl border border-destructive/40 bg-destructive-soft py-2.5 pr-3 pl-4 text-left transition-opacity hover:opacity-90"
      >
        <RefreshCw className="size-4 shrink-0 text-destructive" strokeWidth={2} />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-destructive">
            {errorMsg || 'Failed'} — click to retry
          </p>
        </div>
        <Divider />
        <Badge />
      </button>
    </div>
  )
}

export function hasDoCaptchaKey(): boolean {
  return !!SITE_KEY
}
